import { useEffect, useState, type RefObject } from 'react';

type UseScrollFadeArgs = {
  /** The scrolling box. `axis` picks which of its size and scroll properties are read. */
  outerRef: RefObject<HTMLElement | null>;
  /** The (uncapped, intrinsic-size) content wrapper inside `outerRef`. */
  innerRef: RefObject<HTMLElement | null>;
  /**
   * The scroll direction. `'y'` reads `clientHeight`, `scrollTop` and
   * `scrollHeight` off `outerRef`, `'x'` reads `clientWidth`, `scrollLeft` and
   * `scrollWidth`.
   * @default 'y'
   */
  axis?: 'x' | 'y';
};

type UseScrollFadeResult = {
  /** The content continues past the end edge (bottom, or right). */
  showFade: boolean;
  /** The content has been scrolled away from the start edge (top, or left). */
  showStartFade: boolean;
};

/**
 * Reports whether a scroll container has more content past its end edge
 * (`showFade`) or has been scrolled away from its start edge (`showStartFade`),
 * so the caller can draw a fade at whichever edge has more to reveal. It works
 * on either axis: vertical by default, sideways with `axis: 'x'`.
 *
 * ResizeObserver on both boxes (the inner wrapper's intrinsic size catches
 * content growing even when the outer box doesn't resize; the outer box
 * catches the container itself being resized, e.g. a lens grip drag) + a
 * passive scroll listener that retires the end fade once the tail of the
 * content is on screen. The observer is created inside the effect and
 * disconnected in cleanup, with no lifetime ref, so this is StrictMode-safe by
 * construction.
 */
export function useScrollFade({ outerRef, innerRef, axis = 'y' }: UseScrollFadeArgs): UseScrollFadeResult {
  const [overflowing, setOverflowing] = useState(false);
  const [atEnd, setAtEnd] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const outer = outerRef.current;
    const inner = innerRef.current;
    if (!outer || !inner) return;

    const check = () => {
      if (axis === 'x') {
        setOverflowing(outer.scrollWidth > outer.clientWidth + 1);
        setAtEnd(outer.scrollLeft + outer.clientWidth >= outer.scrollWidth - 1);
        setScrolled(outer.scrollLeft > 0);
        return;
      }
      setOverflowing(inner.scrollHeight > outer.clientHeight + 1);
      setAtEnd(outer.scrollTop + outer.clientHeight >= outer.scrollHeight - 1);
      setScrolled(outer.scrollTop > 0);
    };
    check();

    const ro = new ResizeObserver(check);
    ro.observe(outer);
    ro.observe(inner);
    outer.addEventListener('scroll', check, { passive: true });

    return () => {
      ro.disconnect();
      outer.removeEventListener('scroll', check);
    };
  }, [outerRef, innerRef, axis]);

  return { showFade: overflowing && !atEnd, showStartFade: overflowing && scrolled };
}
